import { SearchRequest } from '@marageti/z4-sdk/lib/entity';
import { PartnerStatus, PartnerAvailability } from '@marageti/z4-sdk/lib/travel';
import { createNewUTCDateFromString } from '@marageti/z4-lib';

// eslint-disable-next-line import/prefer-default-export
export const defaultPartnersSearchRequest = (page: number): SearchRequest => ({
  sortBy: [{
    field: 'name',
    order: 'asc',
  }],
  filterBy: [{
    field: 'status',
    value: [
      PartnerStatus.ACTIVE,
      PartnerStatus.NEW,
      PartnerStatus.PHASING_OUT,
      PartnerStatus.NON_STRATEGIC,
      PartnerStatus.ONBOARDING,
    ],
  }],
  pagination: {
    limit: 30,
    page,
  },
});

/**
 * Filter out PartnerAvailability objects that have an end date older than or equal to the current date.
 * @param availabilities An array of PartnerAvailability objects to filter.
 * @returns An array of PartnerAvailability objects with end dates newer or equal to the current date.
 */
export const filterOutOldData = (availabilities: PartnerAvailability[]): PartnerAvailability[] => {
  const currentDate: Date = new Date();
  const currentYear: number = currentDate.getFullYear();
  const currentMonth: number = currentDate.getMonth();
  const currentDay: number = currentDate.getDate();
  return availabilities?.filter((availability: PartnerAvailability) => {
    const endDate: Date = createNewUTCDateFromString(availability.endDate);
    const endDateYear: number = endDate.getFullYear();
    const endDateMonth: number = endDate.getMonth();
    const endDateDay: number = endDate.getDate();
    return (
      endDateYear > currentYear
      || (endDateYear === currentYear && endDateMonth > currentMonth)
      || (endDateYear === currentYear && endDateMonth === currentMonth && endDateDay >= currentDay)
    );
  });
};

/**
 * Filter PartnerAvailability objects that overlap with the given date's month and year.
 * @param availabilities An array of PartnerAvailability objects to filter.
 * @param date The target date object.
 * @returns An array of PartnerAvailability objects that overlap with the given date's month and year.
 */
export const filterByDateMonthAndYear = (
  availabilities: PartnerAvailability[],
  date: Date,
): PartnerAvailability[] => {
  const targetMonth = date.getMonth();
  const targetYear = date.getFullYear();
  const targetDate = new Date(targetYear, targetMonth, 1);
  const targetMonthEnd = new Date(targetYear, targetMonth + 1, 0);

  return availabilities?.filter((availability: PartnerAvailability) => {
    const startDate: Date = createNewUTCDateFromString(availability.startDate);
    const endDate: Date = createNewUTCDateFromString(availability.endDate);
    return startDate <= targetMonthEnd && endDate >= targetDate;
  });
};

/**
 * @returns A string representing the first day of the current month in the format yyyy-mm-dd
 */
export const getFirstDayOfCurrentMonth = () => {
  const today = new Date();
  const year = today.getFullYear();
  const month = String(today.getMonth() + 1).padStart(2, '0'); // add 1 to month index because it starts at 0
  return `${year}-${month}-01`;
};
